import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
} from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData } from "../../../axios";
import {
  BaseEquipmentDepot,
  EquipmentDepot,
  UpdateEquipmentDepot,
  decodeEquipmentDepot,
  decodeEquipmentDepots,
} from "./types";
import keyBy from "lodash/keyBy";
import { notifyL } from "../../notifications";
import {
  DUPLICATE_EQUIPMENT_DEPOT_NAME,
  EQUIPMENT_DEPOT_HAS_EQUIPMENT,
} from "dora-shared";

const prefix = "data/equipment-depots";

type State = {
  entities: Dictionary<EquipmentDepot>;
  ids: string[];
  // Keyed by depot id. Set when an archive attempt is rejected because
  // equipment is still attached to the depot - shown as the archive button's
  // tooltip rather than only a generic toast, since it's specific to that row.
  archiveErrors: Dictionary<string>;
  loading: boolean;
};

const initialState: State = {
  entities: {},
  ids: [],
  archiveErrors: {},
  loading: false,
};

export const loadEquipmentDepots = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<EquipmentDepot[]> => {
    return await axios
      .get("/api/equipment/depots", { params: { archived: false } })
      .then(getData)
      .then(decodeEquipmentDepots);
  }
);

// The name is unique per org, so the server answers 409
// DUPLICATE_EQUIPMENT_DEPOT_NAME when it is taken. That is a refusal aimed at
// the user: it gets its own translated toast and becomes the rejection value,
// which leaves the dialog open. Everything else is rethrown untouched -
// createErrorReportingAsyncThunk is what reports it (Sentry, reportError, the
// generic toast), and it classifies the failure first, so a dropped connection
// or a 401 is not turned into "unexpected error" on the way past.
export const addEquipmentDepot = createErrorReportingAsyncThunk<
  EquipmentDepot,
  BaseEquipmentDepot,
  { rejectValue: string }
>(`${prefix}/add`, async (values, thunkApi) => {
  try {
    const depot = await axios
      .post("/api/equipment/depots", values)
      .then(getData)
      .then(decodeEquipmentDepot);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "equipmentDepotSaved",
        type: "success",
      })
    );
    return depot;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_EQUIPMENT_DEPOT_NAME
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingEquipmentDepotDuplicateName",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_EQUIPMENT_DEPOT_NAME);
    }
    throw err;
  }
});

// Same contract as add: renaming a depot onto a name that is taken is the only
// refusal this endpoint has of its own.
export const updateEquipmentDepot = createErrorReportingAsyncThunk<
  EquipmentDepot,
  UpdateEquipmentDepot,
  { rejectValue: string }
>(`${prefix}/update`, async (depot, thunkApi) => {
  try {
    const updated = await axios
      .put(`/api/equipment/depots/${depot.id}`, depot)
      .then(getData)
      .then(decodeEquipmentDepot);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "equipmentDepotSaved",
        type: "success",
      })
    );
    return updated;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_EQUIPMENT_DEPOT_NAME
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingEquipmentDepotDuplicateName",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_EQUIPMENT_DEPOT_NAME);
    }
    throw err;
  }
});

export const archiveEquipmentDepot = createErrorReportingAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(`${prefix}/archive`, async (id, thunkApi) => {
  try {
    await axios.post(`/api/equipment/depots/${id}/archive`);
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === EQUIPMENT_DEPOT_HAS_EQUIPMENT
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorArchivingEquipmentDepotHasEquipment",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(EQUIPMENT_DEPOT_HAS_EQUIPMENT);
    }
    throw err;
  }
});

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadEquipmentDepots.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadEquipmentDepots.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadEquipmentDepots.fulfilled, (state, action) => {
        state.loading = false;
        const depots = action.payload;
        state.entities = keyBy(depots, "id");
        state.ids = depots.map((x) => x.id);
      })
      .addCase(addEquipmentDepot.fulfilled, (state, action) => {
        const depot = action.payload;
        state.ids.unshift(depot.id);
        state.entities[depot.id] = depot;
      })
      .addCase(updateEquipmentDepot.fulfilled, (state, action) => {
        const depot = action.payload;
        state.entities[depot.id] = depot;
      })
      .addCase(archiveEquipmentDepot.pending, (state, action) => {
        delete state.archiveErrors[action.meta.arg];
      })
      .addCase(archiveEquipmentDepot.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.entities[id];
        state.ids = state.ids.filter((x) => x !== id);
        delete state.archiveErrors[id];
      })
      .addCase(archiveEquipmentDepot.rejected, (state, action) => {
        if (action.payload === EQUIPMENT_DEPOT_HAS_EQUIPMENT) {
          state.archiveErrors[action.meta.arg] = EQUIPMENT_DEPOT_HAS_EQUIPMENT;
        }
      });
  },
});

export default slice.reducer;
